import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logomark } from "@/components/Logomark";
import { cn } from "@/lib/utils";

const links = [
  { to: "/catalogue", label: "Catalogue" },
  { to: "/governance-model", label: "Governance Model" },
  { to: "/pricing-rules", label: "Pricing & Licensing" },
  { to: "/data-handling", label: "Data Handling" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5">
          <Logomark className="h-10 w-10 shrink-0" />
          <span className="flex items-baseline gap-2">
            <span className="font-display text-xl font-bold tracking-tight text-foreground">
              BIGTEK
            </span>
            <span className="hidden text-[11px] uppercase tracking-widest-xs text-muted-foreground sm:inline">
              Governance Technology
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) =>
                cn(
                  "text-sm transition-colors duration-200 hover:text-[hsl(var(--brand))]",
                  isActive ? "text-foreground font-medium" : "text-muted-foreground"
                )
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/readiness-check"
            data-testid="nav-readiness-check"
            className="text-sm text-muted-foreground transition-colors duration-200 hover:text-[hsl(var(--brand))]"
          >
            Readiness Self-Check
          </Link>
          <Button
            data-testid="header-cta-enquire"
            onClick={() => navigate("/catalogue")}
            className="rounded-sm bg-[hsl(var(--brand))] text-white transition-shadow duration-200 hover:bg-[hsl(217_91%_53%)] hover:glow-primary"
          >
            Enquire <ArrowRight className="ml-1.5 h-4 w-4" strokeWidth={1.75} />
          </Button>
        </nav>

        <div className="flex items-center gap-1 md:hidden">
          <Button
            size="sm"
            data-testid="mobile-cta-enquire"
            onClick={() => navigate("/catalogue")}
            className="h-9 rounded-sm bg-[hsl(var(--brand))] px-4 text-white hover:bg-[hsl(217_91%_53%)]"
          >
            Enquire
          </Button>
          <button
            data-testid="mobile-menu-toggle"
            className="-mr-2 flex h-11 w-11 items-center justify-center text-foreground"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            {open ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border bg-background md:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col px-4 py-2">
            {[...links, { to: "/readiness-check", label: "Readiness Self-Check" }].map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "flex min-h-[48px] items-center justify-between border-b border-border/60 px-1 text-base transition-colors",
                    isActive ? "font-medium text-[hsl(var(--brand))]" : "text-muted-foreground"
                  )
                }
              >
                {l.label}
                <ArrowRight className="h-4 w-4 opacity-40" strokeWidth={1.5} />
              </NavLink>
            ))}
            <Button
              data-testid="mobile-menu-cta"
              onClick={() => {
                setOpen(false);
                navigate("/catalogue");
              }}
              className="mt-4 h-12 w-full rounded-sm bg-[hsl(var(--brand))] text-white hover:bg-[hsl(217_91%_53%)]"
            >
              Enquire about a product <ArrowRight className="ml-1.5 h-4 w-4" strokeWidth={1.75} />
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
}
